import { Center, Loader } from '@mantine/core'
import { useRouterState } from '@tanstack/react-router'
import { startTransition, useCallback, useEffect, useMemo, useState } from 'react'
import { hasBoggleDevSearchFlag, resolveBoggleDevFreeDictionary } from '~/boggle/devMode'
import {
  resolveBoggleChallengeConfigurationPracticeRun,
  resolveBoggleChallengeConfigurationRun,
  resolveBoggleChallengePracticeRun,
  resolveBoggleChallengeRun,
  type BoggleChallengeConfigurationId,
} from '~/boggle/challenges'
import { createBoggleGameState } from '~/boggle/engine/standard/game'
import {
  getBoggleGameModeDefinition,
  getBoggleGameModeRunSettings,
  type BoggleGameModeId,
  type BoggleRunKind,
} from '~/boggle/gameModes'
import { areBogglePlaygroundSettingsEqual } from '~/boggle/lib/playground'
import {
  isBoggleDebugSearchEnabled,
  readBoggleDebugSeed,
} from '~/boggle/ui/state/debugSearch'
import { BOGGLE_CHALLENGE_BORDER_ACCENT_KEY } from '~/boggle/ui/atoms/Buttons'
import { ChallengeRunSurface } from '~/boggle/ui/run/ChallengeRunSurface'
import { StandardRunSurface } from '~/boggle/ui/run/StandardRunSurface'
import { BoggleStore } from '~/boggle/ui/state/Store'
import { useBoggleBrowserBridge } from '~/boggle/ui/state/useBrowserBridge'
import { useBoggleClock } from '~/boggle/ui/state/useClock'
import { useDeferredStandardDictionary } from '~/boggle/ui/state/useDeferredStandardDictionary'
import {
  createBoggleCompletedRunSetupSnapshot,
  createBoggleRunAttemptIdentity,
  type BoggleRunAttemptIdentity,
} from '~/boggle/progress/runCompletion'
import type { BoggleGameState } from '~/boggle/engine/standard/types'
import { captureBoggleRunStarted } from '~/boggle/telemetry/boggleTelemetry'

/**
 * Top-level run host for a chosen game mode + run kind (standard / challenge / challenge-practice).
 * Owns store creation, clock + browser bridge, dictionary gating, and seed/settings sync.
 * Delegates all run UI + storage I/O to a variant surface chosen from `runKind`.
 */
export function RunScreen({
  challengeConfigurationId,
  challengeProgressSeed,
  gameModeId,
  onBackToChallengeStaging,
  onExitRun,
  onNewGame,
  runKind,
  seed,
}: {
  challengeConfigurationId?: BoggleChallengeConfigurationId
  challengeProgressSeed?: number | string
  gameModeId: BoggleGameModeId
  onBackToChallengeStaging?: () => void
  onExitRun: () => void
  onNewGame: () => void
  runKind: BoggleRunKind
  seed: number | string
}) {
  const routerSearch = useRouterState({
    select: s =>
      ((s.location as { search?: Record<string, unknown> }).search ?? {}) as Record<
        string,
        unknown
      >,
  })
  const isDebugRun = isBoggleDebugSearchEnabled(routerSearch)
  const debugSeed = isDebugRun ? readBoggleDebugSeed(routerSearch) : null
  const runSeed = debugSeed ?? seed
  const devAcceptAnyDictionaryWord = resolveBoggleDevFreeDictionary(routerSearch.free)
  const telemetryEnabled =
    !import.meta.env.DEV &&
    !isDebugRun &&
    !hasBoggleDevSearchFlag(routerSearch.dev) &&
    !hasBoggleDevSearchFlag(routerSearch.free)
  const dictionaryReady = useDeferredStandardDictionary(devAcceptAnyDictionaryWord)

  const challengeRun = useMemo(
    () =>
      runKind === 'challenge'
        ? challengeConfigurationId
          ? resolveBoggleChallengeConfigurationRun(
              gameModeId,
              challengeConfigurationId,
              runSeed,
            )
          : resolveBoggleChallengeRun(runSeed)
        : null,
    [challengeConfigurationId, gameModeId, runKind, runSeed],
  )
  const challengePracticeRun = useMemo(
    () =>
      runKind === 'challenge-practice'
        ? challengeConfigurationId
          ? resolveBoggleChallengeConfigurationPracticeRun(
              gameModeId,
              challengeConfigurationId,
              runSeed,
            )
          : resolveBoggleChallengePracticeRun(runSeed)
        : null,
    [challengeConfigurationId, gameModeId, runKind, runSeed],
  )
  const progressSeed = challengeProgressSeed ?? runSeed
  const runConfig = useMemo(() => {
    if (runKind === 'challenge') {
      return {
        resolvedChallenge: challengeRun!,
        seed,
        settings: challengeRun!.settings,
      }
    }

    if (runKind === 'challenge-practice') {
      return {
        resolvedChallenge: challengePracticeRun!,
        seed: challengePracticeRun!.practiceSeed,
        settings: challengePracticeRun!.settings,
      }
    }

    return {
      resolvedChallenge: null,
      seed: runSeed,
      settings: getBoggleGameModeRunSettings(gameModeId),
    }
  }, [challengePracticeRun, challengeRun, gameModeId, runKind, runSeed])
  const runGameModeDefinition = getBoggleGameModeDefinition(gameModeId)
  const createRunSetupSnapshot = useCallback(
    (state: BoggleGameState) =>
      createBoggleCompletedRunSetupSnapshot(state, {
        challenge: runConfig.resolvedChallenge
          ? {
              aceScore: runConfig.resolvedChallenge.aceScore,
              boardDistribution: runConfig.resolvedChallenge.boardDistribution,
              bonuses: runConfig.resolvedChallenge.bonuses,
              configurationId: runConfig.resolvedChallenge.configurationId,
              cores: runConfig.resolvedChallenge.cores.map(core => ({
                id: core.id,
                isRow: core.isRow,
                label: core.label,
                optionId: core.optionId,
                tierLabel: core.tierLabel,
                value: core.value,
              })),
              gameplay: runConfig.resolvedChallenge.gameplay,
              pathMode: runConfig.resolvedChallenge.pathMode,
              progressSeed,
              specialTileReveals: runConfig.resolvedChallenge.specialTileReveals,
              targetScore: runConfig.resolvedChallenge.targetScore,
            }
          : null,
        gameModeCorePreset: runGameModeDefinition.corePreset,
        seedInput: runConfig.seed,
      }),
    [progressSeed, runConfig, runGameModeDefinition.corePreset],
  )
  const [initialState] = useState(() =>
    createBoggleGameState({
      devAcceptAnyDictionaryWord,
      playgroundSettings: runConfig.settings,
      resolvedChallenge: runConfig.resolvedChallenge,
      seed: runConfig.seed,
    }),
  )
  const [store] = useState(() => new BoggleStore(initialState))
  const [runAttempt, setRunAttempt] = useState<BoggleRunAttemptIdentity>(() =>
    createBoggleRunAttemptIdentity(new Date(), createRunSetupSnapshot(initialState)),
  )

  useBoggleClock(store, { enabled: !isDebugRun })
  useBoggleBrowserBridge(store)

  useEffect(() => {
    if (!telemetryEnabled) {
      return
    }

    captureBoggleRunStarted({
      gameModeId,
      runAttempt,
      runKind,
    })
  }, [gameModeId, runAttempt, runKind, telemetryEnabled])

  useEffect(() => {
    const current = store.getState()

    if (
      current.seed !== runConfig.seed ||
      !areBogglePlaygroundSettingsEqual(current.playgroundSettings, runConfig.settings) ||
      current.resolvedChallenge !== runConfig.resolvedChallenge
    ) {
      store.reset({
        playgroundSettings: runConfig.settings,
        resolvedChallenge: runConfig.resolvedChallenge,
        seed: runConfig.seed,
      })
      setRunAttempt(
        createBoggleRunAttemptIdentity(new Date(), createRunSetupSnapshot(store.getState())),
      )
    }
  }, [createRunSetupSnapshot, runConfig, store])

  const handleNewGame = useCallback(() => {
    startTransition(() => {
      if (runKind === 'challenge') {
        store.reset({
          playgroundSettings: runConfig.settings,
          resolvedChallenge: runConfig.resolvedChallenge,
          seed: runConfig.seed,
        })
        setRunAttempt(
          createBoggleRunAttemptIdentity(
            new Date(),
            createRunSetupSnapshot(store.getState()),
          ),
        )
        return
      }

      onNewGame()
    })
  }, [gameModeId, onNewGame, progressSeed, runConfig, runKind, store])

  const handleRestartSeed = useCallback(() => {
    startTransition(() => {
      if (runKind === 'standard') {
        onNewGame()
        return
      }

      store.reset({
        playgroundSettings: runConfig.settings,
        resolvedChallenge: runConfig.resolvedChallenge,
        seed: runConfig.seed,
      })
      setRunAttempt(
        createBoggleRunAttemptIdentity(new Date(), createRunSetupSnapshot(store.getState())),
      )
    })
  }, [
    createRunSetupSnapshot,
    gameModeId,
    onNewGame,
    progressSeed,
    runConfig,
    runKind,
    store,
  ])

  const challengeAccentColor = BOGGLE_CHALLENGE_BORDER_ACCENT_KEY

  if (!dictionaryReady && !devAcceptAnyDictionaryWord) {
    return (
      <Center h="100%" mih={0}>
        <Loader color="gray" type="dots" />
      </Center>
    )
  }

  if (runKind === 'standard') {
    return (
      <StandardRunSurface
        accentColor={runGameModeDefinition.color}
        gameModeId={gameModeId}
        gameRulesDefinition={runGameModeDefinition}
        onExitRun={onExitRun}
        onNewGame={handleNewGame}
        onRestartSeed={handleRestartSeed}
        runAttempt={runAttempt}
        store={store}
        telemetryEnabled={telemetryEnabled}
      />
    )
  }

  const challengeMeta =
    runKind === 'challenge' ? challengeRun : challengePracticeRun
  if (!challengeMeta || !onBackToChallengeStaging) {
    // Defensive: challenge runs should always come with the navigation hook + metadata wired up.
    return null
  }

  const summaryActions =
    runKind === 'challenge'
      ? [{ label: 'Play Again', onClick: handleNewGame, tone: 'challenge' as const }]
      : undefined

  return (
    <ChallengeRunSurface
      accentColor={challengeAccentColor}
      aceScore={challengeMeta.aceScore}
      gameModeId={gameModeId}
      gameRulesDefinition={runGameModeDefinition}
      mode={runKind === 'challenge-practice' ? 'practice' : 'challenge'}
      onBackToChallengeStaging={onBackToChallengeStaging}
      onNewGame={handleNewGame}
      onRestartSeed={handleRestartSeed}
      progressSeed={progressSeed}
      runAttempt={runAttempt}
      store={store}
      summaryActions={summaryActions}
      targetScore={challengeMeta.targetScore}
      telemetryEnabled={telemetryEnabled}
    />
  )
}
